// src/containers/FormBuilderDashboard/components/HeaderEditor.jsx
import React, { useMemo, useState } from 'react';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  Button,
  Typography,
  Stack,
  IconButton,
  MenuItem,
  Checkbox,
  Select,
  FormControl,
  InputLabel,
  Popover,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add,
  Delete,
  ColorLens,
  CheckCircle,
  Cancel,
  Warning,
  Build,
  CarRepair,
  TaskAlt,
  Description,
  AttachMoney,
  CameraAlt,
  Receipt,
  Settings,
  Notifications,
  Star,
  ThumbUp,
  Flag,
  Info,
  Help,
  ContentCopy,
} from '@mui/icons-material';
import HelpTooltip from '../../../components/HelpTooltip';
import { AVAILABLE_FIELDS } from '../constants/fields';
import { HexColorPicker, HexColorInput } from 'react-colorful';

const iconMap = {
  'check-circle': CheckCircle,
  'times-circle': Cancel,
  'exclamation-circle': Warning,
  'wrench': Build,
  'tools': Build,
  'car': CarRepair,
  'clipboard-check': TaskAlt,
  'tasks': Description,
  'dollar-sign': AttachMoney,
  'camera': CameraAlt,
  'receipt': Receipt,
  'cog': Settings,
  'bell': Notifications,
  'star': Star,
  'thumbs-up': ThumbUp,
  'flag': Flag,
  'info-circle': Info,
  'question-circle': Help,
};

const ICON_OPTIONS = Object.keys(iconMap);

// All available input types (no more forced boolean-only)
const INPUT_TYPE_OPTIONS = [
  'checkbox',
  'userstamp',
  'text',
  'textarea',
  'select',
  'select-wide',
  'radio',
  'icon',
];

const userstampFields = [
  'pass', 'recommend', 'required', 'approved', 'declined', 'completed',
  'labor_rate', 'labor_hours', 'labor_total', 'parts', 'parts_total',
  'quantity', 'estimate_total', 'counter', 'left_front', 'right_front',
  'left_rear', 'right_rear', 'front_actual', 'front_min', 'rear_actual', 'rear_min',
];

const getDefaultInputType = (field) => {
  if (userstampFields.includes(field)) return 'userstamp';
  if (field === 'name' || field === 'description') return 'text';
  return 'checkbox'; // safe default for most boolean-style columns
};

const HeaderEditor = ({ sectionId, headers, setHeaders, default_profiles, sections }) => {
  const [colorAnchorEl, setColorAnchorEl] = useState(null);
  const [editingColorIndex, setEditingColorIndex] = useState(null);
  const [copyAllOpen, setCopyAllOpen] = useState(false);

  const handleHeaderChange = (index, key, value) => {
    setHeaders(prev => {
      const updated = [...(prev[sectionId] || [])];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, [sectionId]: updated };
    });
  };

  const handleAddHeader = () => {
    const usedFields = (headers[sectionId] || []).map(h => h.field);
    const availableField = AVAILABLE_FIELDS.find(f => !usedFields.includes(f.value));
    const defaultField = availableField ? availableField.value : `custom_header_${(headers[sectionId]?.length || 0) + 1}`;
    const defaultLabel = availableField ? availableField.label : defaultField.replace(/_/g, ' ').toUpperCase();

    setHeaders(prev => ({
      ...prev,
      [sectionId]: [
        ...(prev[sectionId] || []),
        {
          id: `header_${sectionId}_${Date.now()}`,
          field: defaultField,
          label: defaultLabel,
          inputtype: getDefaultInputType(defaultField),
          include_in_print: true,
          print_label: '',
          allowed_profiles: '',
          icon: 'check-circle',
          true_color: '#4caf50',
        },
      ],
    }));
  };

  const handleCopyToAll = () => {
    const currentHeaders = headers[sectionId] || [];

    setHeaders(prev => {
      const newHeaders = { ...prev };
      Object.keys(sections).forEach(secId => {
        if (secId !== sectionId) {
          newHeaders[secId] = currentHeaders.map(h => ({ ...h }));
        }
      });
      return newHeaders;
    });

    setCopyAllOpen(false);
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'label',
      header: 'Label',
      size: 150,
      enableEditing: true,
      muiEditTextFieldProps: ({ row }) => ({
        onChange: (event) => handleHeaderChange(row.index, 'label', event.target.value),
      }),
    },
    {
      accessorKey: 'field',
      header: 'Field',
      size: 200,
      enableEditing: true,
      Edit: ({ row }) => (
        <FormControl fullWidth>
          <InputLabel>Field</InputLabel>
          <Select
            value={headers[sectionId][row.index].field}
            onChange={(event) => {
              const field = event.target.value;
              const defaultLabel = AVAILABLE_FIELDS.find(f => f.value === field)?.label || field.replace(/_/g, ' ').toUpperCase();
              let newField = field;

              // Avoid duplicate fields
              if (headers[sectionId].some((h, i) => i !== row.index && h.field === field)) {
                const baseField = field.replace(/_\d+$/, '');
                let counter = 1;
                while (headers[sectionId].some(h => h.field === newField)) {
                  newField = `${baseField}_${counter++}`;
                }
              }

              handleHeaderChange(row.index, 'field', newField);
              handleHeaderChange(row.index, 'label', defaultLabel);
              handleHeaderChange(row.index, 'inputtype', getDefaultInputType(newField));
            }}
          >
            {AVAILABLE_FIELDS.map(f => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
      Cell: ({ cell }) => (
        <Typography>
          {AVAILABLE_FIELDS.find(f => f.value === cell.getValue())?.label || cell.getValue()}
        </Typography>
      ),
    },
    {
      accessorKey: 'allowed_profiles',
      header: 'Allowed Profiles',
      size: 200,
      enableEditing: true,
      // ... (your existing allowed_profiles column - unchanged)
      Edit: ({ row }) => (
        <FormControl fullWidth>
          <InputLabel>Allowed Profiles</InputLabel>
          <Select
            multiple
            value={headers[sectionId][row.index].allowed_profiles ? headers[sectionId][row.index].allowed_profiles.split(',').map(String) : []}
            onChange={(event) => {
              const profiles = Array.isArray(event.target.value) ? event.target.value : [];
              handleHeaderChange(row.index, 'allowed_profiles', profiles.join(','));
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.length === 0 ? (
                  <Box sx={{ color: 'text.secondary' }}>All</Box>
                ) : (
                  selected.map(id => (
                    <Box key={id} sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.5 }}>
                      {default_profiles.find(p => p.id === Number(id))?.name || `ID:${id}`}
                    </Box>
                  ))
                )}
              </Box>
            )}
          >
            <MenuItem value="">
              <Checkbox checked={!headers[sectionId][row.index].allowed_profiles} />
              <em>All</em>
            </MenuItem>
            {default_profiles.map(p => (
              <MenuItem key={p.id} value={String(p.id)}>
                <Checkbox checked={headers[sectionId][row.index].allowed_profiles?.split(',').includes(String(p.id))} />
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
      Cell: ({ cell }) => (
        <Typography>
          {cell.getValue()
            ? cell.getValue().split(',').map(id => default_profiles.find(p => p.id === Number(id))?.name || `ID:${id}`).join(', ')
            : 'All'}
        </Typography>
      ),
    },
    {
      accessorKey: 'inputtype',
      header: 'Input Type',
      size: 160,
      enableEditing: true,
      Edit: ({ row }) => (
        <FormControl fullWidth>
          <InputLabel>Input Type</InputLabel>
          <Select
            value={headers[sectionId][row.index].inputtype || getDefaultInputType(headers[sectionId][row.index].field)}
            onChange={(event) => handleHeaderChange(row.index, 'inputtype', event.target.value)}
          >
            {INPUT_TYPE_OPTIONS.map(type => (
              <MenuItem key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
      Cell: ({ cell }) => (
        <Typography>{cell.getValue() || 'checkbox'}</Typography>
      ),
    },
    {
      accessorKey: 'include_in_print',
      header: 'Print',
      size: 80,
      enableEditing: false,
      Cell: ({ row }) => (
        <Checkbox
          checked={headers[sectionId][row.index]?.include_in_print !== false}
          onChange={e => handleHeaderChange(row.index, 'include_in_print', e.target.checked)}
          inputProps={{ 'aria-label': 'Include in printed form' }}
        />
      ),
    },
    {
      accessorKey: 'print_label',
      header: 'Print Label',
      size: 160,
      enableEditing: true,
      muiEditTextFieldProps: ({ row }) => ({
        placeholder: headers[sectionId][row.index]?.label || '',
        onChange: event => handleHeaderChange(row.index, 'print_label', event.target.value),
      }),
      Cell: ({ row }) => {
        const h = headers[sectionId][row.index] || {};
        return h.print_label
          ? <Typography>{h.print_label}</Typography>
          : <Typography color="textSecondary">{h.label}</Typography>;
      },
    },
    {
      accessorKey: 'icon',
      header: 'Icon',
      size: 140,
      Cell: ({ row }) => {
        const iconName = headers[sectionId][row.index]?.icon || 'check-circle';
        const Icon = iconMap[iconName] || CheckCircle;
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Icon sx={{ fontSize: 28, color: '#555' }} />
          </Box>
        );
      },
      Edit: ({ row }) => {
        const currentIcon = headers[sectionId][row.index]?.icon || 'check-circle';
        return (
          <Select
            value={currentIcon}
            onChange={(e) => handleHeaderChange(row.index, 'icon', e.target.value)}
            renderValue={(selected) => {
              const Icon = iconMap[selected] || CheckCircle;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon />
                  <span>{selected}</span>
                </Box>
              );
            }}
          >
            {ICON_OPTIONS.map(icon => {
              const Icon = iconMap[icon];
              return (
                <MenuItem key={icon} value={icon}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText primary={icon} />
                </MenuItem>
              );
            })}
          </Select>
        );
      },
    },
    {
      accessorKey: 'true_color',
      header: 'True Color',
      size: 120,
      Cell: ({ row }) => {
        const color = headers[sectionId][row.index]?.true_color || '#4caf50';
        return (
          <Box
            onClick={(e) => {
              setColorAnchorEl(e.currentTarget);
              setEditingColorIndex(row.index);
            }}
            sx={{
              width: 40,
              height: 40,
              bgcolor: color,
              borderRadius: 1,
              border: '2px solid #ccc',
              mx: 'auto',
              cursor: 'pointer',
              boxShadow: 2,
              '&:hover': { transform: 'scale(1.1)', boxShadow: 6 },
            }}
          />
        );
      },
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 80,
      enableEditing: false,
      Cell: ({ row }) => (
        <IconButton
          size="small"
          color="error"
          onClick={() => {
            setHeaders(prev => ({
              ...prev,
              [sectionId]: prev[sectionId].filter((_, i) => i !== row.index),
            }));
          }}
        >
          <Delete fontSize="small" />
        </IconButton>
      ),
    },
  ], [headers, sectionId, default_profiles]);

  const headerData = useMemo(
    () => (headers[sectionId] || []).map((header, index) => ({ ...header, index })),
    [headers, sectionId]
  );

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Headers</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button size="small" startIcon={<Add />} onClick={handleAddHeader}>
            Add Header
          </Button>
          <Button
            size="small"
            startIcon={<ContentCopy />}
            onClick={() => setCopyAllOpen(true)}
            variant="outlined"
            color="primary"
          >
            Copy to All Sections
          </Button>
          <HelpTooltip title="Add a column | Copy current headers to all sections" />
        </Stack>
      </Stack>

      <MaterialReactTable
        columns={columns}
        data={headerData}
        enableRowOrdering
        enableEditing
        editDisplayMode="cell"
        getRowId={(row) => row.id || row.index}
        muiRowDragHandleProps={({ table }) => ({
          onDragEnd: () => {
            const { draggingRow, hoveredRow } = table.getState();
            if (hoveredRow && draggingRow) {
              const newData = [...table.getRowModel().rows];
              newData.splice(hoveredRow.index, 0, newData.splice(draggingRow.index, 1)[0]);
              const updatedHeaders = newData.map(row => ({
                id: row.original.id,
                field: row.original.field,
                label: row.original.label,
                inputtype: row.original.inputtype,
                include_in_print: row.original.include_in_print,
                print_label: row.original.print_label,
                allowed_profiles: row.original.allowed_profiles,
                icon: row.original.icon,
                true_color: row.original.true_color,
              }));
              setHeaders(prev => ({ ...prev, [sectionId]: updatedHeaders }));
            }
          },
        })}
        initialState={{ density: 'compact' }}
      />

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorAnchorEl)}
        anchorEl={colorAnchorEl}
        onClose={() => { setColorAnchorEl(null); setEditingColorIndex(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 3 }}>
          <HexColorPicker
            color={editingColorIndex !== null ? (headers[sectionId][editingColorIndex]?.true_color || '#4caf50') : '#4caf50'}
            onChange={(color) => {
              if (editingColorIndex !== null) handleHeaderChange(editingColorIndex, 'true_color', color);
            }}
          />
          <HexColorInput
            color={editingColorIndex !== null ? (headers[sectionId][editingColorIndex]?.true_color || '#4caf50') : '#4caf50'}
            onChange={(color) => {
              if (editingColorIndex !== null) handleHeaderChange(editingColorIndex, 'true_color', color);
            }}
            prefixed
            style={{ width: '100%', marginTop: 12, padding: 8 }}
          />
        </Box>
      </Popover>

      {/* Copy to All Dialog */}
      <Dialog open={copyAllOpen} onClose={() => setCopyAllOpen(false)}>
        <DialogTitle>Copy Headers to All Sections?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will replace the headers in <strong>all other sections</strong> with the current section's headers. 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyAllOpen(false)}>Cancel</Button>
          <Button onClick={handleCopyToAll} variant="contained" color="primary">
            Yes, Copy to All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HeaderEditor;